(() => {
  'use strict';
  // Match the renderer's structural guard; never use location as a canonical.
  const publicUrl = value => {
    if (typeof value !== 'string' || !value || /[\s\\]|__|unbound|binding.required/i.test(value)) return null;
    try {
      const url = new URL(value), hostname = url.hostname.toLowerCase().replace(/\.$/, '');
      if (url.protocol !== 'https:' || url.username || url.password || (url.port && url.port !== '443')) return null;
      if (!hostname.includes('.') || !/^[a-z0-9.-]+$/.test(hostname) || /^[\d.]+$/.test(hostname)) return null;
      if (/(^|\.)(localhost|local|internal|intranet|test|invalid|example|onion)$/.test(hostname) || /(^|\.)(example\.(com|org|net)|home\.arpa)$/.test(hostname)) return null;
      if (!hostname.split('.').every(label => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label))) return null;
      return url.href;
    } catch { return null; }
  };
  const makeLinks = (canonical, title, description, image) => {
    if (!canonical) return {};
    const text = [title, description].filter(Boolean).join(' — ');
    const link = (base, params) => `${base}?${new URLSearchParams(params)}`;
    const links = {facebook:link('https://www.facebook.com/sharer/sharer.php',{u:canonical}),whatsapp:link('https://wa.me/',{text:`${text}\n${canonical}`}),telegram:link('https://t.me/share/url',{url:canonical,text}),x:link('https://x.com/intent/tweet',{url:canonical,text:title})};
    const media = publicUrl(image);
    if (media) links.pinterest = link('https://www.pinterest.com/pin/create/button/',{url:canonical,media,description:text});
    return links;
  };
  const init = root => {
    let dictionary={}; try { dictionary=JSON.parse(root.closest('[data-field-guide]')?.querySelector('[data-fg-i18n]')?.textContent||'{}'); } catch {}
    const t = source => dictionary[source] || source;
    if (root.dataset.fgShareReady) return;
    root.dataset.fgShareReady = 'true';
    const title = root.dataset.fgShareTitle || '', description = root.dataset.fgShareDescription || '';
    const canonical = publicUrl(root.dataset.fgShareCanonical);
    const status = root.querySelector('[data-fg-share-status]');
    const say = message => { if (status) status.textContent = message; };
    const linkGroup = root.querySelector('[data-fg-share-link]'), field = root.querySelector('[data-fg-share-url]');
    const copy = root.querySelector('[data-fg-share-copy]'), device = root.querySelector('[data-fg-share-device]');
    const fileButton = root.querySelector('[data-fg-share-file]');
    let localLink = '';
    try { if (/^https?:$/.test(location.protocol)) localLink = location.href; } catch { /* Keep manual browser-address fallback. */ }
    if (field && linkGroup && (canonical || localLink)) {
      field.value = canonical || localLink;
      linkGroup.hidden = false;
      root.querySelector('[data-fg-share-link-label]').textContent = canonical ? t('Public guide link') : t('Local review link');
      field.addEventListener('click', () => field.select());
      copy.hidden = false;
      copy.textContent = canonical ? t('Copy link') : t('Copy local review link');
      copy.addEventListener('click', async () => {
        // Unbound local review URLs may only enter this explicitly labelled copy
        // action, never a network link or navigator.share URL payload.
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          try { await navigator.clipboard.writeText(field.value); say(canonical ? t('Link copied.') : t('Local review link copied. It works only where this review is available.')); return; }
          catch { /* The visible selection is the supported manual fallback. */ }
        }
        field.focus(); field.select(); field.setSelectionRange(0, field.value.length);
        say(t('Select and copy the link shown above. Automatic copying is not available in this browser.'));
      });
    }
    const links = makeLinks(canonical, title, description, root.dataset.fgShareImage);
    root.querySelectorAll('[data-fg-share-network]').forEach(control => {
      const key = control.dataset.fgShareNetwork, href = links[key];
      if (href) {
        const anchor = document.createElement('a');
        anchor.className = control.className; anchor.dataset.fgShareNetwork = key;
        anchor.href = href; anchor.target = '_blank'; anchor.rel = 'noopener noreferrer nofollow';
        anchor.setAttribute('aria-label', t('Share on {network} (opens a new tab)').replace('{network}',control.textContent.replace('↗','').trim()));
        anchor.append(...Array.from(control.childNodes)); control.replaceWith(anchor);
      } else if (control.tagName === 'A') {
        // A stale or edited attribute must not leave an active local URL route.
        const button = document.createElement('button');
        button.type = 'button'; button.disabled = true; button.className = control.className;
        button.dataset.fgShareNetwork = key; button.append(...Array.from(control.childNodes)); control.replaceWith(button);
      }
    });
    if (canonical) {
      root.querySelector('[data-fg-share-note]').textContent = t('Choose where to share. Each service opens its own sharing screen; you decide what to send.');
      root.querySelector('[data-fg-share-pinterest-note]').hidden = Boolean(links.pinterest);
      if (device && typeof navigator.share === 'function') {
        device.hidden = false;
        device.addEventListener('click', async () => {
          try { await navigator.share({title, text:description, url:canonical}); say(t('Your device has handled the share request.')); }
          catch (error) { if (error.name !== 'AbortError') say(t('Your device could not open sharing. Copy the link or choose a service instead.')); }
        });
      }
    }
    let preparedFile = null, preparing = false;
    const prepareFile = async () => {
      if (preparedFile || preparing || !fileButton || typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function' || typeof File !== 'function') return;
      preparing = true;
      try {
        const source = root.dataset.fgShareDownload || '';
        const relativeAsset = /^assets\/[a-zA-Z0-9_./-]+\.(png|jpe?g|webp)$/i.test(source) && !source.split('/').includes('..');
        const publicImage = canonical && publicUrl(source);
        if (!relativeAsset && !publicImage) return;
        const image = new URL(source, location.href), sameOrigin = image.origin === location.origin;
        // Local reviews fetch only their own relative asset. A deliberately
        // bound public page may fetch its explicit public CDN image via CORS;
        // credentials and redirects never cross that boundary.
        if ((!sameOrigin && !publicImage) || !/^https?:$/.test(image.protocol) || !/\.(png|jpe?g|webp)$/i.test(image.pathname)) return;
        const response = await fetch(image.href, {credentials:sameOrigin ? 'same-origin' : 'omit', mode:sameOrigin ? 'same-origin' : 'cors', redirect:'error'});
        if (!response.ok) return;
        const blob = await response.blob();
        if (!/^image\/(png|jpeg|webp)$/.test(blob.type) || !blob.size) return;
        const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/jpeg' ? 'jpg' : 'webp';
        const file = new File([blob], `bebru-${root.dataset.fgShareId || 'hair-guide'}.${extension}`, {type:blob.type});
        if (navigator.canShare({files:[file]})) { preparedFile = file; fileButton.hidden = false; }
      } catch { /* Download remains available when file sharing is unsupported. */ }
      finally { preparing = false; }
    };
    root.addEventListener('toggle', () => { if (root.open) prepareFile(); });
    if (root.open) prepareFile();
    fileButton?.addEventListener('click', async () => {
      if (!preparedFile) return;
      try { await navigator.share({files:[preparedFile], title}); say(t('Your device has handled the image share request.')); }
      catch (error) { if (error.name !== 'AbortError') say(t('Your device could not share the image. Download it and attach it yourself instead.')); }
    });
  };
  const start = () => document.querySelectorAll('[data-fg-share]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true}); else start();
})();
